import { Flex, Center } from '@chakra-ui/react';
import {
  AccordionContent,
  AccordionItem,
  AccordionRoot,
  AccordionTrigger,
  Button,
  Text,
} from '@kula-ai/components';
import { DocusignWordmarkAsset } from '@kula-ai/assets';
import { decode } from 'js-base64';
import { useHistory } from 'react-router-dom';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { ESIGN_NOTIFICATION_COPY } from '@/constants/esignature';
import { NotificationCard } from '../components/Card';
import { NotificationTime } from '../components/Time';

interface EsignNotificationData {
  esign_document?: {
    id?: number;
    document_names?: string[];
    stage_name?: string | null;
    url?: string | null;
  };
  candidate?: { id?: number; name?: string };
  job?: { id?: number; title?: string; url?: string | null };
  application?: { id?: number; url?: string | null };
}

interface EsignDocumentNotificationProps {
  notification?: Record<string, any>;
  onClose?: () => void;
}

/** One labelled row of the Details block. */
const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Flex flexDir="column" mb="6px">
    <Text variant="small" className="!font-semibold">
      {label}
    </Text>
    <Text variant="small">{value}</Text>
  </Flex>
);

/**
 * The six e-sign events (RTC types 126–131) as one in-app card.
 *
 * One component, registered six times: the payload is identical across all of
 * them and `title` arrives fully rendered from the server, so there is nothing
 * per-event to branch on. A seventh event would be a map entry, not a file.
 *
 * The title is rendered as plain text. The mocks bold both party names, but the
 * payload carries only `candidate.name` — the employer's name is not in it, so
 * bolding what we have would emphasise one half of a two-name sentence and
 * leave the other flat, which reads worse than neither. Ask Core for
 * `employer_name` and this becomes a substring highlight.
 *
 * Nullability is the load-bearing part: `stage_name` is null for an ad-hoc send
 * (nothing configured it, so there is no stage to name) and every url is
 * omitted when the recipient cannot open that application. Both are deliberate
 * on the backend's side — the notification still says what happened, it just
 * does not offer a door that would 403.
 */
export const EsignDocumentNotification = ({
  notification = {},
  onClose = () => {},
}: EsignDocumentNotificationProps) => {
  const history = useHistory();
  const [markAsRead] = useMarkAsReadMutation();

  let data: EsignNotificationData = {};
  try {
    data = JSON.parse(decode(notification.custom_attributes ?? '')) ?? {};
  } catch {
    // A card that cannot parse its payload still shows the title and time,
    // which is the part that tells the recruiter what happened.
    data = {};
  }

  const { esign_document: envelope, candidate, job } = data;
  const documents = envelope?.document_names ?? [];
  const stageName = envelope?.stage_name;

  // The envelope's own deep link, falling back to the application's.
  const target = envelope?.url || data.application?.url || null;

  const handleGoToCandidate = () => {
    if (!target) return;
    onClose();
    try {
      markAsRead({ id: notification?.id });
    } catch {
      // Navigation is the point; a failed read-receipt must not block it.
    }
    // Absolute urls from the API, so strip the origin to keep it a client-side
    // route rather than a full page load.
    try {
      const { pathname, search } = new URL(target);
      history.push(`${pathname}${search}`);
    } catch {
      window.location.assign(target);
    }
  };

  return (
    <NotificationCard notification={notification}>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="46px" h="46px" borderRadius="full" bg="ats-tertiary">
          {/* The mocks show a calendar, which RTC flagged as a placeholder —
              every other e-sign surface in the app uses the Docusign mark, so
              this follows those rather than the mock. Worth confirming. */}
          <DocusignWordmarkAsset width={20} height={20} />
        </Center>

        <Flex flexDir="column" ml="16px" gap="8px" alignSelf="start" minW={0}>
          <Text variant="small">{notification?.title}</Text>

          {/* Open by default, matching the mocks: the detail is the reason the
              card is worth expanding, and a closed block hides all of it. */}
          <AccordionRoot type="multiple" defaultValue={['details']}>
            <AccordionItem value="details">
              <AccordionTrigger className="w-full min-w-0 [&>span]:min-w-0">
                <Text variant="small" className="!font-semibold">
                  {ESIGN_NOTIFICATION_COPY.DETAILS}
                </Text>
              </AccordionTrigger>
              <AccordionContent>
                <Flex
                  border="1px solid"
                  borderColor="border-color"
                  borderRadius="sm"
                  p="14px"
                  flexDir="column"
                  mt="6px"
                >
                  <DetailRow
                    label={ESIGN_NOTIFICATION_COPY.CANDIDATE}
                    value={candidate?.name ?? '—'}
                  />
                  {documents.length > 0 && (
                    <DetailRow
                      label={ESIGN_NOTIFICATION_COPY.DOCUMENT}
                      // An envelope can carry up to ten.
                      value={documents.join(', ')}
                    />
                  )}
                  {job?.title && (
                    <DetailRow
                      label={ESIGN_NOTIFICATION_COPY.JOB_NAME}
                      value={job.title}
                    />
                  )}
                  {/* Omitted rather than shown empty: an ad-hoc send has no
                      configured stage, so "Stage name: —" would invent one. */}
                  {stageName && (
                    <DetailRow
                      label={ESIGN_NOTIFICATION_COPY.STAGE_NAME}
                      value={stageName}
                    />
                  )}
                </Flex>
              </AccordionContent>
            </AccordionItem>
          </AccordionRoot>

          {/* No target means the recipient cannot open this application. The
              card still reports what happened; it just offers no dead door. */}
          {target && (
            <Button
              variant="primary"
              onClick={handleGoToCandidate}
              data-test-id="esign-notification-cta"
            >
              {ESIGN_NOTIFICATION_COPY.GO_TO_CANDIDATE}
            </Button>
          )}

          <NotificationTime notification={notification} />
        </Flex>
      </Flex>
    </NotificationCard>
  );
};
